import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, X } from "lucide-react";
import SearchReports from "@/components/deck/SearchReports";
import Slide from "@/components/deck/Slide";
import DraggableSlidesList from "@/components/deck/DraggableSlidesList";
import ReportDesignerV2 from "./ReportDesignerV2";
import type { Deck as DeckType } from "@/common/decks";
import { slideFromReportId } from "@/common/decks";
import { useDeckReducer } from "@/hooks/useDeckReducer";
import { getDeck, updateDeck } from "@/lib/serverComm";
import { exportDeckAsPptx } from "@/lib/deckExport";
import EventBus from "@/lib/eventBus";
import { ReportForUI } from "@/types/report";
import mixpanel from "@/lib/mixpanel";

const Deck: React.FC = () => {
  const [isAddSlideModalOpen, setIsAddSlideModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedReports, setSelectedReports] = useState<ReportForUI[]>([]);
  const [deck, dispatch] = useDeckReducer(null);
  const prevDeckRef = useRef<DeckType | null>(null);
  const prevReportIdRef = useRef<string | undefined>();
  const navigate = useNavigate();
  const DECK_POLLING_INTERVAL = 3000; // Polling interval for pending/processing slides

  // Debounce timeout ref for saving deck
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  // Polling interval ref for pending/processing slides
  const pollingIntervalRef = useRef<ReturnType<typeof setTimeout>>();
  // Ref to track saving state to avoid dependency cycles
  const isSavingRef = useRef(false);

  const { id, reportId } = useParams<{ id: string; reportId?: string }>();
  const isReportModalOpen = !!reportId;

  useEffect(() => {
    const fetchDeck = async () => {
      if (!id) return;
      try {
        const fetchedDeck = await getDeck(id);
        dispatch({ type: "SET_DECK", payload: fetchedDeck });
      } catch (error) {
        console.error("Error fetching deck:", error);
      }
    };

    if (id) {
      fetchDeck();
    }
  }, [id, dispatch]);

  // Handle modal state changes - emit refresh event when modal closes
  useEffect(() => {
    // If we had a reportId before and now we don't, the modal was closed
    if (prevReportIdRef.current && !reportId) {
      console.log(
        `Modal closed, emitting report:refresh for reportId: ${prevReportIdRef.current}`,
      );
      EventBus.$emit("report:refresh", prevReportIdRef.current);
    }

    prevReportIdRef.current = reportId;
  }, [reportId]);

  const handleSpeakerChange = useCallback(
    (slideId: string, content: string) => {
      dispatch({
        type: "UPDATE_SPEAKER_NOTES",
        payload: { slideId, content },
      });
    },
    [dispatch],
  );

  const handleMoveSlideUp = useCallback(
    (slideId: string) => {
      dispatch({
        type: "MOVE_SLIDE_UP",
        payload: slideId,
      });
    },
    [dispatch],
  );

  const handleMoveSlideDown = useCallback(
    (slideId: string) => {
      dispatch({
        type: "MOVE_SLIDE_DOWN",
        payload: slideId,
      });
    },
    [dispatch],
  );

  const handleEditSlide = useCallback(
    (slideId: string) => {
      const slide = deck?.slides.find((s) => s.id === slideId);
      if (slide?.report_id) {
        mixpanel.track("Edit Deck Report", {
          reportId: slide.report_id,
          eventDescription: slide.title,
        });
        navigate(`/decks/${id}/reports/${slide.report_id}`);
      }
    },
    [deck?.slides, id, navigate],
  );

  const handleCloseReportModal = useCallback(() => {
    // Just navigate - the useEffect will handle the EventBus emission
    navigate(`/decks/${id}`);
  }, [id, navigate]);

  const handleDeleteSlide = useCallback(
    (slideId: string) => {
      dispatch({
        type: "DELETE_SLIDE",
        payload: slideId,
      });
    },
    [dispatch],
  );

  const handleTitleChange = useCallback(
    (slideId: string, title: string) => {
      dispatch({
        type: "UPDATE_SLIDE_TITLE",
        payload: { slideId, title },
      });
    },
    [dispatch],
  );

  const handleDeckTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({
        type: "UPDATE_DECK_TITLE",
        payload: e.target.value,
      });
    },
    [dispatch],
  );

  const handleSlideReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      console.log(`Reorder slide from ${fromIndex} to ${toIndex}`);
      dispatch({
        type: "REORDER_SLIDES",
        payload: { fromIndex, toIndex },
      });
    },
    [dispatch],
  );

  const handleExportPptx = useCallback(async () => {
    if (!deck) return;

    try {
      setIsExporting(true);
      await exportDeckAsPptx(deck);
    } catch (error) {
      console.error("Error exporting deck to PPTX:", error);
    } finally {
      setIsExporting(false);
    }
  }, [deck]);

  const allSlidesCompleted =
    deck?.slides.every((slide) => slide.status === "complete") ?? false;

  const handleCloseAddSlideModal = useCallback(() => {
    setIsAddSlideModalOpen(false);
    setSelectedReports([]);
  }, []);

  const handleAddReportsToDeck = useCallback(() => {
    if (selectedReports.length === 0) return;

    const newSlides = selectedReports.map((report) => {
      return slideFromReportId(report.id, { title: report.title });
    });

    dispatch({
      type: "ADD_SLIDE",
      payload: newSlides,
    });

    // Reset selected reports and close modal
    setSelectedReports([]);
    setIsAddSlideModalOpen(false);
  }, [selectedReports, dispatch]);

  const saveDeck = useCallback(async () => {
    if (!deck || isSavingRef.current) {
      return; // No deck or already saving
    }
    if (
      prevDeckRef.current &&
      JSON.stringify(prevDeckRef.current) === JSON.stringify(deck)
    ) {
      return; // Deck hasn't changed, no need to save
    }

    prevDeckRef.current = deck; // Update previous deck state
    try {
      isSavingRef.current = true;
      setIsSaving(true);
      console.log("Saving deck...");
      const { deck: returnedDeck, changed_slides } = await updateDeck(deck);
      dispatch({ type: "SET_DECK", payload: returnedDeck });
      console.log("Changed slides:", changed_slides);

      isSavingRef.current = false;
      setIsSaving(false);
      console.log("Deck saved successfully");
    } catch (error) {
      console.error("Error saving deck:", error);
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [deck, dispatch]);

  // First useEffect: Debounced save for deck changes
  useEffect(() => {
    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (!deck) return;

    // Set debounced save timeout for any deck changes
    debounceTimeoutRef.current = setTimeout(saveDeck, 1000);

    // Cleanup timeout on unmount or dependency change
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [deck, saveDeck]);

  // Second useEffect: Polling for pending/processing slides
  useEffect(() => {
    // Clear previous interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    if (!deck) return;

    // Check if any slides have pending or processing status
    const hasPendingSlides = deck.slides.some(
      (slide) => slide.status === "pending" || slide.status === "processing",
    );

    if (hasPendingSlides) {
      // Set up polling interval for pending/processing slides
      console.log("Setting up polling interval for pending/processing slides");
      pollingIntervalRef.current = setInterval(async () => {
        if (!id) return;
        const resp = await getDeck(id);
        dispatch({ type: "RECONCILE_PENDING_STATUSES", payload: resp });
      }, DECK_POLLING_INTERVAL);
    }

    // Cleanup interval on unmount or dependency change
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [deck, id, dispatch]);

  if (!deck) {
    return (
      <div className="flex justify-center items-center h-screen text-deck-primary">
        Loading...
      </div>
    );
  }

  return (
    // Main Container
    <div className="flex bg-deck-bg font-dm_sans antialiased text-base">
      {/* Add Slide Modal */}
      {isAddSlideModalOpen && (
        <div className="absolute flex justify-center items-center z-50 w-screen h-screen bg-black/50">
          <div className="relative bg-white rounded-xl shadow-slide-card w-5/12">
            <X
              className="absolute right-2 top-2 text-deck-primary cursor-pointer"
              size={24}
              onClick={handleCloseAddSlideModal}
            />
            <div className="block px-8 pt-8">
              <span className="block text-deck-primary text-4xl font-bold mb-6">
                Add a slide
              </span>
            </div>

            <div className="block px-8">
              <SearchReports
                onChange={(reports) => setSelectedReports(reports)}
              />
            </div>

            <div className="flex justify-between px-8 py-4 border-t border-deck-500">
              <button
                className="transition px-4 py-2 bg-white border border-deck-500 text-deck-primary rounded-full font-semibold hover:bg-deck-primary hover:text-white"
                onClick={handleCloseAddSlideModal}
              >
                Cancel
              </button>
              <div className="flex gap-x-4">
                <button className="transition px-4 py-2 bg-white border border-deck-primary text-deck-primary rounded-full font-semibold hover:bg-deck-primary hover:text-white">
                  Create a Report
                </button>
                <button
                  className={`transition px-4 py-2 rounded-full font-semibold ${
                    selectedReports.length === 0
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-deck-primary text-white hover:bg-deck-primary/80"
                  }`}
                  disabled={selectedReports.length === 0}
                  onClick={handleAddReportsToDeck}
                >
                  Add To Deck
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report Designer Modal */}
      {isReportModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="relative bg-white rounded-xl shadow-2xl w-[95vw] h-[95vh] max-w-8xl pt-2">
            <X
              className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 cursor-pointer z-10"
              size={24}
              onClick={handleCloseReportModal}
            />
            <div className="w-full h-full overflow-hidden rounded-xl">
              <ReportDesignerV2 />
            </div>
          </div>
        </div>
      )}

      {/* Left Sidebar */}
      <div className="w-20 bg-white shadow-md"></div>

      {/* Main Content Area */}
      <div className="flex-1 p-8 min-h-screen">
        {/* Header */}
        <header className="flex items-center justify-start gap-x-6 mb-8">
          <Link to="/decks" className="flex items-center gap-x-2">
            <ArrowLeft size={20} />
            <span className="text-deck-primary font-semibold">Back</span>
          </Link>
          <h1 className="text-4xl font-semibold text-deck-primary">
            Deck Preview
          </h1>
          <div className="flex justify-between gap-x-4 ml-auto">
            <button
              className="transition px-4 py-2 border-2 border-deck-primary text-deck-primary rounded-full font-semibold hover:bg-deck-primary hover:text-white"
              onClick={() => setIsAddSlideModalOpen(true)}
            >
              Add a Slide
            </button>
            <button
              className="transition px-4 py-2 bg-deck-primary text-white rounded-full font-semibold hover:bg-deck-primary/80 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleExportPptx}
              disabled={isExporting || !deck || !allSlidesCompleted}
            >
              {isExporting ? "Exporting..." : "Export Deck"}
            </button>
          </div>
        </header>

        {/* Input Title */}
        <div className="w-[944px] ml-80 mb-6">
          <label
            className="block text-deck-primary text-base font-semibold mb-2"
            htmlFor="deck-title-input"
          >
            Deck Title
          </label>
          <div className="flex items-center justify-between">
            <input
              id="deck-title-input"
              type="text"
              placeholder="Title of Deck"
              value={deck.name}
              onChange={handleDeckTitleChange}
              className="transition w-[564px] px-3 py-2 bg-white border border-deck-borders rounded-lg focus:outline-deck-primary"
            />
            <span className="text-sm text-deck-primary">
              {isSaving
                ? "Saving..."
                : `Saved at ${deck.updated_at.toLocaleTimeString()}`}
            </span>
          </div>
        </div>

        {/* SideBar & Slide Cards */}
        <div className="flex gap-x-8">
          {/* Sidebar */}
          <DraggableSlidesList
            slides={deck.slides}
            onReorder={handleSlideReorder}
          />

          {/* Slide Cards */}
          <div className="max-w-[1300px] flex-grow flex flex-col gap-y-6 overflow-y-auto max-h-[calc(100vh_-_250px)]">
            {deck.slides.map((slide) => (
              <Slide
                key={slide.id}
                id={slide.id}
                title={slide.title}
                speakerNotes={slide.speaker_notes}
                status={slide.status}
                reportId={slide.report_id}
                onTitleChange={handleTitleChange}
                onSpeakerNotesChange={handleSpeakerChange}
                onMoveUp={handleMoveSlideUp}
                onMoveDown={handleMoveSlideDown}
                onEdit={handleEditSlide}
                onDelete={handleDeleteSlide}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
export default Deck;
